import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { getFleetDocumentTypesAPI } from "services/api/fleet/vehicles/documents/api";
import { initialFleetVehicleDocumentSearchForm } from "services/api/fleet/vehicles/documents/consts";
import {
  FleetDocumentSearchFormValues,
  FleetDocumentType,
} from "services/api/fleet/vehicles/documents/types";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";

import { VehicleDocumentSearchFilterProps } from "./types";

const VehicleDocumentSearchFilters: React.FC<
  VehicleDocumentSearchFilterProps
> = (props) => {
  const { onSearchChange } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetDocumentSearchFormValues>(
    initialFleetVehicleDocumentSearchForm
  );
  const [documentTypes, setDocumentTypes] = useState<FleetDocumentType[]>([]);

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  const fetchDocumentTypes = async () => {
    try {
      setDocumentTypes(await getFleetDocumentTypesAPI());
    } catch {
      setDocumentTypes([]);
    }
  };

  // Svaka promjena filtra odmah pokreće pretragu — stranica ne drži vlastito stanje filtra.
  const applyChange = useCallback(
    (values: FleetDocumentSearchFormValues) => {
      setFormValues(values);
      onSearchChange(values);
    },
    [onSearchChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      applyChange({ ...formValues, search: e.target.value });
    },
    [formValues, applyChange]
  );

  const handleDocumentTypeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      applyChange({ ...formValues, documentTypeId: item.value || null });
    },
    [formValues, applyChange]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      applyChange({ ...formValues, [name]: value });
    },
    [formValues, applyChange]
  );

  const handleResetFormClick = useCallback(() => {
    applyChange(initialFleetVehicleDocumentSearchForm);
  }, [applyChange]);

  const documentTypeItems: DropdownOption[] = documentTypes.map((type) => ({
    value: type.id,
    text: type.name,
  }));

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[240px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleSearchChange}
          placeholder={t("admin.search.placeholder")}
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      {/* Nazivi vrsta dokumenata su dugi, pa polje uzima dvostruku širinu. */}
      <div className="min-w-[400px]">
        <Dropdown
          name="documentTypeId"
          placeholder="Vrsta dokumenta"
          options={documentTypeItems}
          value={formValues.documentTypeId}
          onChange={handleDocumentTypeChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="createdFrom"
          label="Uneseno od - do"
          value={formValues.createdFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="createdTo"
          value={formValues.createdTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="flex items-center pb-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default memo(VehicleDocumentSearchFilters);
